import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProductPage() {
  const { slug } = useParams();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState({});
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setQty(1);
    setActiveImage(0);
    api
      .product(slug)
      .then((p) => {
        setProduct(p);
        const first = p.variants?.find((v) => v.available) || p.variants?.[0];
        setSelected(first?.options || {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const currentVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    const match = product.variants.find((v) => Object.entries(selected).every(([k, val]) => v.options[k] === val));
    return match || product.variants[0];
  }, [product, selected]);

  if (loading) {
    return (
      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div className="aspect-[4/5] animate-pulse rounded-2xl bg-neutral-200/70" />
        <div className="space-y-4 pt-8">
          <div className="h-8 w-2/3 animate-pulse rounded bg-neutral-200/70" />
          <div className="h-5 w-1/3 animate-pulse rounded bg-neutral-200/70" />
          <div className="h-24 w-full animate-pulse rounded bg-neutral-200/70" />
        </div>
      </main>
    );
  }

  if (notFound || !product) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-2xl font-bold">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-sm font-semibold underline underline-offset-4">
          Back to Shop
        </Link>
      </main>
    );
  }

  const price = currentVariant?.price ?? product.price;
  const compareAt = currentVariant?.compare_at_price ?? product.compare_at_price;
  const discount = compareAt && compareAt > price ? Math.round((1 - price / compareAt) * 100) : 0;
  const inStock = currentVariant?.available ?? product.available;
  const images = product.images?.length ? product.images : [product.image];

  const addToBag = () => {
    if (!inStock) return;
    add(
      {
        slug: product.slug,
        name: product.name,
        price,
        image: product.image,
        variant_id: currentVariant?.id,
        variant_title: currentVariant && currentVariant.title !== "Default Title" ? currentVariant.title : "",
      },
      qty
    );
    toast.success(`${product.name} added to your bag`, { action: { label: "View Bag", onClick: () => setOpen(true) } });
    setOpen(true);
  };

  return (
    <main data-testid="product-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <nav className="mb-8 text-xs text-neutral-400" data-testid="product-breadcrumb">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/shop?category=${product.category}`} className="hover:text-neutral-900">{product.category_name}</Link>
        <span className="mx-2">/</span>
        <span className="text-neutral-700">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="lg:sticky lg:top-24">
            <div className="img-frame relative aspect-[4/5] overflow-hidden rounded-2xl">
              {images[activeImage] && <img src={images[activeImage]} alt={product.name} className="h-full w-full object-cover" />}
              {product.badge && (
                <span className="absolute left-4 top-4 rounded-full bg-neutral-900 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  {product.badge}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-3" data-testid="product-gallery">
                {images.map((img, i) => (
                  <button
                    key={img + i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className={`img-frame h-16 w-16 overflow-hidden rounded-lg border-2 transition-colors ${i === activeImage ? "border-neutral-900" : "border-transparent"}`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl" data-testid="product-name">
            {product.name}
          </h1>
          {product.vendor && <p className="mt-2 text-xs uppercase tracking-[0.18em] text-neutral-400">{product.vendor}</p>}

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-heading text-3xl font-bold text-neutral-900" data-testid="product-price">{inr(price)}</span>
            {compareAt && compareAt > price && (
              <>
                <span className="text-lg text-neutral-400 line-through">{inr(compareAt)}</span>
                <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800">Save {discount}%</span>
              </>
            )}
          </div>
          <p className="mt-1 text-xs text-neutral-400">Inclusive of all taxes</p>

          <p className={`mt-4 text-sm font-medium ${inStock ? "text-emerald-700" : "text-red-700"}`} data-testid="availability-status">
            {inStock ? "In stock — ships within 24 hours" : "Currently out of stock"}
          </p>

          {product.options?.filter((opt) => opt.values.length > 1).map((opt) => (
            <div key={opt.name} className="mt-6">
              <p className="mb-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">
                {opt.name}: <span className="text-neutral-900">{selected[opt.name]}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {opt.values.map((val) => {
                  const variantForValue = product.variants.find((v) => v.options[opt.name] === val);
                  const valueAvailable = variantForValue?.available;
                  return (
                    <button
                      key={val}
                      data-testid={`variant-${opt.name.toLowerCase()}-${val.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                        selected[opt.name] === val
                          ? "border-neutral-900 bg-neutral-900 text-white"
                          : "border-neutral-200 text-neutral-700 hover:border-neutral-900"
                      } ${valueAvailable === false ? "opacity-40 line-through" : ""}`}
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {product.description_html ? (
            <div
              className="mt-6 max-w-lg text-sm leading-relaxed text-neutral-600 sm:text-base [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: product.description_html }}
              data-testid="product-description"
            />
          ) : (
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-neutral-600 sm:text-base" data-testid="product-description">
              {product.description}
            </p>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center rounded-full border border-neutral-300">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 px-4 transition-colors hover:text-neutral-500" aria-label="Decrease quantity">
                −
              </button>
              <span className="w-6 text-center text-sm font-semibold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} className="p-3 px-4 transition-colors hover:text-neutral-500" aria-label="Increase quantity">
                +
              </button>
            </div>
            <button
              data-testid="add-to-bag-button"
              onClick={addToBag}
              disabled={!inStock}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-neutral-900 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
            >
              <ShoppingBag size={16} /> {inStock ? `Add to Bag — ${inr(price * qty)}` : "Out of Stock"}
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-neutral-500">
            <span className="flex items-center gap-1.5"><Truck size={14} /> Free shipping over ₹999</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> 7-day easy returns</span>
          </div>

          <Accordion type="single" collapsible className="mt-8 w-full" data-testid="product-details-accordion">
            <AccordionItem value="details" className="border-b border-neutral-200">
              <AccordionTrigger className="py-4 text-left font-heading text-sm font-semibold hover:no-underline">Product Details</AccordionTrigger>
              <AccordionContent className="pb-4 text-sm leading-relaxed text-neutral-500">
                Sold by {product.vendor || "Everyday Essentials"}. Every product is quality-checked before dispatch and packed in
                recyclable, plastic-free packaging.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" className="border-b border-neutral-200">
              <AccordionTrigger className="py-4 text-left font-heading text-sm font-semibold hover:no-underline">Shipping &amp; Returns</AccordionTrigger>
              <AccordionContent className="pb-4 text-sm leading-relaxed text-neutral-500">
                Orders ship within 24 hours. Metro delivery in 2–4 business days; rest of India in 4–6. Free shipping over ₹999,
                otherwise a flat ₹79. 7-day hassle-free returns on damaged or defective items.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Reveal>
      </div>

      {product.related?.length > 0 && (
        <section className="mt-20 sm:mt-28" data-testid="related-products">
          <Reveal>
            <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">You May Also Like</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">
            {product.related.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.05} y={20}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
